import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import NotificationBell from './NotificationBell';
import { toTitleCase } from '../utils/titleCase';
import { searchService } from '../services/services';
import './Navbar.css';

// Session timeout: warn 3 minutes before JWT expiry
const SESSION_WARN_BEFORE_MS = 3 * 60 * 1000;

const Navbar = ({ toggleSidebar }) => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  // ── Quick Search ──────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const searchDebounce = useRef(null);

  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setSearchResults([]); return; }
    try {
      setSearchLoading(true);
      const r = await searchService.search(q.trim());
      const results = Array.isArray(r?.data) ? r.data : (r?.results || r?.data?.results || []);
      setSearchResults(results.slice(0, 8));
    } catch { setSearchResults([]); }
    finally { setSearchLoading(false); }
  }, []);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setSearchOpen(true);
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => runSearch(q), 350);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      setSearchOpen(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
    if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); }
  };

  const handleResultClick = (result) => {
    setSearchOpen(false);
    setSearchQuery('');
    if (result.type === 'case' || result._type === 'case') navigate(`/cases/${result._id}`);
    else if (result.type === 'evidence' || result._type === 'evidence') navigate(`/evidence/${result._id}`);
    else if (result.type === 'personnel' || result._type === 'personnel') navigate(`/personnel`);
    else navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Session Timeout Warning ───────────────────────────────────────────────
  const [sessionWarning, setSessionWarning] = useState(false);
  const [sessionSecondsLeft, setSessionSecondsLeft] = useState(0);

  useEffect(() => {
    if (!user) return;
    // Try to read exp from JWT in cookie/localStorage
    let expMs = null;
    try {
      const token = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('token='));
      if (token) {
        const payload = JSON.parse(atob(token.split('=')[1].split('.')[1]));
        expMs = payload.exp * 1000;
      }
    } catch { /* no token accessible */ }
    if (!expMs) return;

    const checkSession = () => {
      const msLeft = expMs - Date.now();
      if (msLeft <= 0) { logout(); return; }
      if (msLeft <= SESSION_WARN_BEFORE_MS) {
        setSessionWarning(true);
        setSessionSecondsLeft(Math.floor(msLeft / 1000));
      } else {
        setSessionWarning(false);
      }
    };

    checkSession();
    const interval = setInterval(checkSession, 10000);
    return () => clearInterval(interval);
  }, [user, logout]);

  return (
    <>
      {/* Session timeout warning banner */}
      {sessionWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#7c2d12', borderBottom: '2px solid #ef4444',
          padding: '10px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span style={{ color: '#fca5a5', fontWeight: 700, fontSize: '0.9rem' }}>
            ⚠️ Your session expires in {Math.floor(sessionSecondsLeft / 60)}m {sessionSecondsLeft % 60}s. Save your work.
          </span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => window.location.reload()}
              style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', padding: '5px 14px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
              Refresh Session
            </button>
            <button onClick={() => setSessionWarning(false)}
              style={{ background: 'transparent', color: '#fca5a5', border: '1px solid #ef444466', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', fontSize: '0.82rem' }}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <nav className="navbar" style={{ marginTop: sessionWarning ? '44px' : '0' }}>
        <div className="navbar-left">
          <button className="menu-btn" onClick={toggleSidebar}>☰</button>
          <div className="navbar-brand">
            <svg className="navbar-emblem" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2L20 5V12C20 16 16 20 12 22C8 20 4 16 4 12V5L12 2Z" fill="none" stroke="currentColor" strokeWidth="1.6" />
              <path d="M12 6.4L13.5 9.6L17 9.9L14.4 12.2L15.2 15.6L12 13.8L8.8 15.6L9.6 12.2L7 9.9L10.5 9.6L12 6.4Z" fill="currentColor" />
            </svg>
            <h1 className="navbar-title">CaseFile Creator</h1>
          </div>
        </div>

        {/* Quick Search */}
        <div ref={searchRef} className="navbar-search-bar" style={{ position: 'relative', flex: '0 1 340px', margin: '0 16px' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '0.85rem', pointerEvents: 'none' }}>🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => searchQuery.length >= 2 && setSearchOpen(true)}
              placeholder="Quick search cases, evidence..."
              style={{
                width: '100%', boxSizing: 'border-box',
                background: '#0f172a', border: '1px solid #334155', borderRadius: '8px',
                color: '#e2e8f0', padding: '7px 12px 7px 32px', fontSize: '0.85rem', outline: 'none',
              }}
            />
            {searchLoading && (
              <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b', fontSize: '0.75rem' }}>...</span>
            )}
          </div>
          {searchOpen && (searchResults.length > 0 || searchQuery.length >= 2) && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 1000,
              background: '#1e293b', border: '1px solid #334155', borderRadius: '10px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)', overflow: 'hidden'
            }}>
              {searchResults.length === 0 && !searchLoading ? (
                <div style={{ padding: '14px 16px', color: '#64748b', fontSize: '0.85rem' }}>No results for "{searchQuery}"</div>
              ) : (
                searchResults.map((r, i) => (
                  <div key={r._id || i} onClick={() => handleResultClick(r)}
                    style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '10px' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#334155'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 7px', borderRadius: '8px', background: 'rgba(59,130,246,0.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.3)', textTransform: 'uppercase', flexShrink: 0 }}>
                      {r.type || r._type || 'result'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.title || r.name || r.caseNumber || r.description || 'Untitled'}
                      </p>
                      {r.caseNumber && r.title && <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b' }}>{r.caseNumber}</p>}
                    </div>
                  </div>
                ))
              )}
              <div onClick={() => { setSearchOpen(false); navigate(`/search?q=${encodeURIComponent(searchQuery)}`); }}
                style={{ padding: '10px 16px', color: '#3b82f6', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textAlign: 'center', borderTop: '1px solid #334155' }}
                onMouseEnter={e => e.currentTarget.style.background = '#1e3a5f'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                See all results for "{searchQuery}" →
              </div>
            </div>
          )}
        </div>

        <div className="navbar-right">
          <NotificationBell />
          <button
            className="btn btn-secondary"
            onClick={() => setTheme(theme === 'midnight' ? 'slate' : 'midnight')}
            title={theme === 'midnight' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            style={{ fontSize: '1rem', padding: '6px 10px', minWidth: 'unset' }}
          >
            {theme === 'midnight' ? '☀️' : '🌙'}
          </button>
          <span className="user-info">
            {user?.role === 'owner' ? (
              <>
                <span className="user-info-name">{toTitleCase(user?.fullName)}</span>
                <span className="user-info-owner-badge">SYSTEM OWNER</span>
              </>
            ) : (
              <>
                <span className="user-info-name">{toTitleCase(user?.rank)} {toTitleCase(user?.fullName)} ({user?.badgeNumber})</span>
                {(user?.agencyName || user?.agency?.name) && (
                  <span className="user-info-agency">{user?.agencyName || user?.agency?.name}</span>
                )}
              </>
            )}
          </span>
          <button className="btn btn-secondary" onClick={logout}>Logout</button>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
